import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";
import VideoSection from "@/components/VideoSection";
import PropertyCardGallery from "@/components/PropertyCardGallery";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import HowToBookSection from "@/components/HowToBookSection";
import MapSection from "@/components/MapSection";
import PageStickyNav from "@/components/PageStickyNav";

const CATEGORY_COPY = {
  hotel: {
    fr: { num: "I", over: "Chapitre I", title: "Hôtels", lede: "Huit adresses entre la porte du désert, les sommets de l'Atlas et le ksar d'Aït Ben Haddou — dont trois projets à venir." },
    en: { num: "I", over: "Chapter I", title: "Hotels", lede: "Eight addresses between the door of the desert, the Atlas summits and the ksar of Aït Ben Haddou — including three upcoming projects." },
    es: { num: "I", over: "Capítulo I", title: "Hoteles", lede: "Ocho direcciones entre la puerta del desierto, las cumbres del Atlas y el ksar de Aït Ben Haddou — incluyendo tres proyectos próximos." },
  },
  bivouac: {
    fr: { num: "II", over: "Chapitre II", title: "Camps & Bivouacs du désert", lede: "Des haimas privées aux camps capables d'accueillir mille invités sous les étoiles de l'Erg Chebbi." },
    en: { num: "II", over: "Chapter II", title: "Desert Camps & Bivouacs", lede: "From private haimas to camps capable of welcoming a thousand guests beneath the stars of Erg Chebbi." },
    es: { num: "II", over: "Capítulo II", title: "Campamentos y Bivouacs del desierto", lede: "Desde haimas privadas hasta campamentos capaces de acoger a mil invitados bajo las estrellas del Erg Chebbi." },
  },
  refuge: {
    fr: { num: "III", over: "Chapitre III", title: "Refuge de montagne", lede: "Un refuge isolé dans les canyons du M'Goun, accessible à pied ou à dos de mule." },
    en: { num: "III", over: "Chapter III", title: "Mountain Refuge", lede: "A remote refuge in the M'Goun canyons, accessible on foot or by mule." },
    es: { num: "III", over: "Capítulo III", title: "Refugio de montaña", lede: "Un refugio aislado en los cañones del M'Goun, accesible a pie o en mula." },
  },
};

const COMING_SOON_LABEL = {
  fr: "Ouverture prochaine",
  en: "Coming soon",
  es: "Próxima apertura",
};

const HotelsPage = () => {
  const { t, lang } = useLanguage();
  const comingSoonLabel = COMING_SOON_LABEL[lang] || COMING_SOON_LABEL.en;

  const hotels = HOTELS.filter((h) => h.category === "hotel");
  const bivouacs = HOTELS.filter((h) => h.category === "bivouac");
  const refuges = HOTELS.filter((h) => h.category === "refuge");

  const renderGroup = (items, key) => {
    const c = CATEGORY_COPY[key][lang] || CATEGORY_COPY[key].en;
    return (
      <section
        id={`category-${key}`}
        className="py-16 md:py-24 border-t border-[#2C2621]/10 scroll-mt-28"
        data-testid={`category-${key}`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Chapter header */}
          <div className="grid md:grid-cols-12 gap-6 md:gap-8 mb-10 md:mb-14 items-end">
            <div className="md:col-span-1">
              <div
                className="font-serif-x italic text-5xl md:text-6xl leading-none text-[#C16542]"
                data-testid={`chapter-${key}-numeral`}
              >
                {c.num}
              </div>
            </div>
            <div className="md:col-span-6 md:col-start-3">
              <p className="overline">{c.over}</p>
              <h2 className="font-serif-x mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                {c.title}
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9">
              <p className="text-base md:text-lg text-[#5C5248] leading-relaxed">
                {c.lede}
              </p>
            </div>
          </div>

          {/* Uniform equitable grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {items.map((h) => (
              <PropertyCardGallery
                key={h.slug}
                hotel={h}
                lang={lang}
                ctaLabel={t("common.view_hotel")}
                aspect="aspect-[4/5]"
                testIdPrefix="hotel-list-card"
                comingSoon={!!h.coming_soon}
                comingSoonLabel={comingSoonLabel}
              />
            ))}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div data-testid="page-hotels" className="bg-[#FDFBF7]">
      {/* Page intro */}
      <section className="relative py-24 md:py-32 bg-[#F2EBE1]/60 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline">{t("sections.hotels_overline")}</p>
          <h1 className="font-serif-x mt-4 text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02]">
            {t("sections.hotels_title")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#5C5248] leading-relaxed">
            {t("sections.hotels_sub")}
          </p>

          {/* Category anchor nav */}
          <nav
            className="mt-10 flex flex-wrap gap-2"
            aria-label={lang === "fr" ? "Naviguer par catégorie" : lang === "es" ? "Navegar por categoría" : "Browse by category"}
            data-testid="hotels-category-nav"
          >
            {[
              { key: "hotel", label: { fr: "Hôtels", en: "Hotels", es: "Hoteles" } },
              { key: "bivouac", label: { fr: "Camps & Bivouacs", en: "Camps & Bivouacs", es: "Campamentos y Vivacs" } },
              { key: "refuge", label: { fr: "Refuge", en: "Refuge", es: "Refugio" } },
            ].map((tab) => (
              <a
                key={tab.key}
                href={`#category-${tab.key}`}
                className="border border-[#2C2621]/25 hover:border-[#C16542] hover:text-[#C16542] px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase transition-colors"
                data-testid={`hotels-nav-${tab.key}`}
              >
                {tab.label[lang] || tab.label.en}
              </a>
            ))}
            <Link
              to="/bivouacs"
              className="border border-[#C16542] bg-[#C16542] text-[#FDFBF7] hover:bg-[#A35133] hover:border-[#A35133] px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase transition-colors inline-flex items-center gap-2"
              data-testid="hotels-bivouacs-link"
            >
              {{
                fr: "La page Bivouacs",
                en: "Bivouacs page",
                es: "Página de Vivacs",
              }[lang] || "Bivouacs page"}
              <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </nav>
        </div>
      <BrandMonogramEdge size="xl" opacity={0.6} testIdPrefix="page-hero-brand" />
      </section>

      <PageStickyNav
        items={[
          { id: "category-hotel", label: { en: "Hotels", fr: "Hôtels", es: "Hoteles" } },
          { id: "category-bivouac", label: { en: "Bivouacs", fr: "Bivouacs", es: "Vivacs" } },
          { id: "category-refuge", label: { en: "Refuge", fr: "Refuge", es: "Refugio" } },
        ]}
      />

      {/* Interactive map — same design/behaviour as the /home map. Shows
          every Xaluca hotel, bivouac and refuge so guests can see at a
          glance how the addresses spread across the south of Morocco,
          the Atlas and the desert. On /hotels the "landmarks" filter
          starts off — the visitor is here to focus on the addresses. */}
      <MapSection initialFilters={{ hotel: true, bivouac: true, refuge: true, landmark: false }} />

      {renderGroup(hotels, "hotel")}
      {renderGroup(bivouacs, "bivouac")}
      {renderGroup(refuges, "refuge")}

      {/* PORTFOLIO VIDEOS — a short film tour of the group */}
      <VideoSection
        overline={{ en: "On film", fr: "En images", es: "En imágenes" }}
        title={{
          en: "Eight houses, one Sahara.",
          fr: "Huit maisons, un même Sahara.",
          es: "Ocho casas, un mismo Sáhara.",
        }}
        intro={{
          en: "Short films across the portfolio — the kasbahs, the bivouacs, the mountain refuge — to help you picture where you'd like to stay.",
          fr: "Courts films à travers le portefeuille — les kasbahs, les bivouacs, le refuge de montagne — pour imaginer où vous aimeriez séjourner.",
          es: "Cortos del portafolio — las kasbahs, los vivacs, el refugio de montaña — para imaginar dónde le gustaría alojarse.",
        }}
        featured={{
          id: "hotels-featured",
          title: {
            en: "A grand tour of Grup Xaluca",
            fr: "Le grand tour Grup Xaluca",
            es: "El gran tour de Grup Xaluca",
          },
          desc: {
            en: "From Erfoud's date palms to the M'Goun canyons — a five-minute film that shows the through-line between every house.",
            fr: "Des palmeraies d'Erfoud aux canyons du M'Goun — un film de cinq minutes qui montre le fil rouge entre nos adresses.",
            es: "De los palmerales de Erfoud a los cañones del M'Goun — un film de cinco minutos que muestra el hilo conductor entre cada casa.",
          },
          duration: "5:12",
          tag: { en: "Group film", fr: "Film du groupe", es: "Film del grupo" },
          poster: hotels[0]?.image,
        }}
        secondary={[
          {
            id: "hotels-kasbah",
            title: { en: "Kasbahs of the south", fr: "Kasbahs du sud", es: "Kasbahs del sur" },
            desc: { en: "Earthen walls, palm groves, hammams.", fr: "Murs de pisé, palmeraies, hammams.", es: "Muros de tapial, palmerales, hammams." },
            duration: "2:08",
            tag: { en: "Hotels", fr: "Hôtels", es: "Hoteles" },
            poster: hotels[1]?.image || hotels[0]?.image,
          },
          {
            id: "hotels-bivouac",
            title: { en: "Camps under the Milky Way", fr: "Camps sous la Voie lactée", es: "Campamentos bajo la Vía Láctea" },
            desc: { en: "Haimas, fires, the slow night.", fr: "Haimas, feux, la nuit lente.", es: "Haimas, hogueras, la noche lenta." },
            duration: "2:34",
            tag: { en: "Bivouacs", fr: "Bivouacs", es: "Vivacs" },
            poster: bivouacs[0]?.image || hotels[0]?.image,
          },
          {
            id: "hotels-refuge",
            title: { en: "Mountain refuge", fr: "Refuge de montagne", es: "Refugio de montaña" },
            desc: { en: "On foot or by mule into the M'Goun.", fr: "À pied ou à dos de mule dans le M'Goun.", es: "A pie o en mula por el M'Goun." },
            duration: "1:54",
            tag: { en: "Refuge", fr: "Refuge", es: "Refugio" },
            poster: refuges[0]?.image || hotels[0]?.image,
          },
        ]}
        testid="hotels-videos"
      />

      {/* Steps-to-book — same reusable section shown on Home, Booking
          and Hotel-detail pages so guests get a consistent framing of
          what happens after they submit an enquiry, regardless of
          where they land in the funnel. */}
      <HowToBookSection />
    </div>
  );
};

export default HotelsPage;
